import type { TypeGender } from '@/types/gender.type'

/**
 * Склонение русских ФИО по падежам.
 *
 * Использование:
 *
 *   const cases = fullNameCases('Иванов Иван Иванович', 'm')
 *   // { nom, gen, dat, acc, inst, prep }
 *
 *   const cases = fullNameCases('Коновалова Мария', 'f')
 */

// Предлоги русского языка — не склоняются в составе названий организаций.
const PREPOSITIONS = new Set([
  'без', 'благодаря', 'в', 'вместо', 'вопреки', 'вокруг',
  'для', 'до', 'за', 'из', 'к', 'кроме', 'между',
  'на', 'над', 'насчёт', 'навстречу', 'о', 'об', 'около',
  'от', 'перед', 'по', 'под', 'после', 'пред', 'при',
  'про', 'с', 'у', 'через',
])

function isPreposition (word: string): boolean {
  return PREPOSITIONS.has(word.toLowerCase())
}

export interface INameCases {
  nom: string
  gen: string
  dat: string
  acc: string
  inst: string
  prep: string
}

// Аббревиатуры ОПФ и учреждений — не склоняются.
const ORG_ABBREVIATIONS = new Set([
  'ООО', 'АО', 'ПАО', 'ЗАО', 'ОАО', 'ИП', 'КФХ',
  'АНО', 'НАО', 'НКО',
  'МБОУ', 'МБДОУ', 'МБУДО', 'МБУ',
  'ГБОУ', 'ГБДОУ', 'ГБУДО', 'ГБУ',
  'МАОУ', 'МАУДО', 'МАУ',
  'ГАОУ', 'ГАУДО', 'ГАУ',
  'ФГБОУ', 'ФГАОУ', 'ФГБУ', 'ФГАУ',
  'ЧОП', 'ЧОО', 'ГБР', 'ВО', 'ФГУП'
])

// Известные ОПФ полным текстом с заранее записанными падежами.
// Список отсортирован длиннее → короче, чтобы «публичное акционерное общество»
// проверялось раньше «акционерное общество».
const ORG_FORM_ENTRIES: Array<{ key: string; cases: INameCases }> = [
  {
    key: 'общество с ограниченной ответственностью',
    cases: {
      nom: 'Общество с ограниченной ответственностью',
      gen: 'Общества с ограниченной ответственностью',
      dat: 'Обществу с ограниченной ответственностью',
      acc: 'Общество с ограниченной ответственностью',
      inst: 'Обществом с ограниченной ответственностью',
      prep: 'Обществе с ограниченной ответственностью',
    },
  },
  {
    key: 'публичное акционерное общество',
    cases: {
      nom: 'Публичное акционерное общество',
      gen: 'Публичного акционерного общества',
      dat: 'Публичному акционерному обществу',
      acc: 'Публичное акционерное общество',
      inst: 'Публичным акционерным обществом',
      prep: 'Публичном акционерном обществе',
    },
  },
  {
    key: 'закрытое акционерное общество',
    cases: {
      nom: 'Закрытое акционерное общество',
      gen: 'Закрытого акционерного общества',
      dat: 'Закрытому акционерному обществу',
      acc: 'Закрытое акционерное общество',
      inst: 'Закрытым акционерным обществом',
      prep: 'Закрытом акционерном обществе',
    },
  },
  {
    key: 'открытое акционерное общество',
    cases: {
      nom: 'Открытое акционерное общество',
      gen: 'Открытого акционерного общества',
      dat: 'Открытому акционерному обществу',
      acc: 'Открытое акционерное общество',
      inst: 'Открытым акционерным обществом',
      prep: 'Открытом акционерном обществе',
    },
  },
  {
    key: 'акционерное общество',
    cases: {
      nom: 'Акционерное общество',
      gen: 'Акционерного общества',
      dat: 'Акционерному обществу',
      acc: 'Акционерное общество',
      inst: 'Акционерным обществом',
      prep: 'Акционерном обществе',
    },
  },
  {
    key: 'индивидуальный предприниматель',
    cases: {
      nom: 'Индивидуальный предприниматель',
      gen: 'Индивидуального предпринимателя',
      dat: 'Индивидуальному предпринимателю',
      acc: 'Индивидуального предпринимателя',
      inst: 'Индивидуальным предпринимателем',
      prep: 'Индивидуальном предпринимателе',
    },
  },
]

function invariant (s: string): INameCases {
  return { nom: s, gen: s, dat: s, acc: s, inst: s, prep: s }
}

/**
 * Склоняет название организации по всем падежам.
 *
 * Правила обработки:
 *  - Текст в кавычках «…» не склоняется (торговое название).
 *  - Аббревиатуры (ООО, АО, МБОУ и др.) не склоняются.
 *  - Известные ОПФ полным текстом склоняются по встроенному словарю.
 *  - Предлоги (в, с, для, …) не склоняются.
 *  - Всё остальное склоняется пословно по правилам lastNameCases.
 */
function orgNameCases (orgName: string): INameCases {
  const name = orgName.trim()

  // Извлечь часть в «ёлочках» — она неизменяема во всех падежах.
  const quoteMatch = name.match(/«[^»]*»/)
  const quotedPart = quoteMatch ? quoteMatch[0] : ''
  const prefix = quoteMatch ? name.substring(0, quoteMatch.index).trimEnd() : name

  const join = (form: string) => [form, quotedPart].filter(Boolean).join(' ')

  // 1. Только кавычки, без префикса.
  if (!prefix) return invariant(name)

  // 2. Первое слово — аббревиатура (ООО «…», ФГУП Атлант, ГБР Север и т.п.).
  //    Весь префикс не склоняется: аббревиатура + любое число слов после неё.
  const firstWord = (prefix.split(/\s+/)[0] ?? '').toUpperCase()
  if (ORG_ABBREVIATIONS.has(firstWord)) return invariant(join(prefix))

  // 3. Известная ОПФ полным текстом.
  // Допускается хвост после ОПФ (напр. «Общество с ограниченной ответственностью Ромашка»
  // без кавычек) — хвост трактуется как неизменяемое название.
  const lowerPrefix = prefix.toLowerCase()
  for (const entry of ORG_FORM_ENTRIES) {
    if (lowerPrefix === entry.key || lowerPrefix.startsWith(entry.key + ' ')) {
      const trailing = lowerPrefix.startsWith(entry.key + ' ')
        ? prefix.substring(entry.key.length).trimStart()
        : ''
      const joinAll = (form: string) => [form, trailing, quotedPart].filter(Boolean).join(' ')
      const c = entry.cases
      return {
        nom: joinAll(c.nom),
        gen: joinAll(c.gen),
        dat: joinAll(c.dat),
        acc: joinAll(c.acc),
        inst: joinAll(c.inst),
        prep: joinAll(c.prep),
      }
    }
  }

  // 4. Общий случай: пословное склонение, предлоги не трогаем.
  const wordCases = prefix.split(/\s+/).filter(Boolean).map((word) =>
    isPreposition(word) ? invariant(word) : lastNameCases(word),
  )

  const result = {} as INameCases
  for (const c of ['nom', 'gen', 'dat', 'acc', 'inst', 'prep'] as const) {
    result[c] = join(wordCases.map((wc) => wc[c]).join(' '))
  }
  return result
}

// Просклонять фамилию из именительного в остальные падежи.
// В некоторых случаях пригодится хинт gender, равный "f" или (по умолчанию) "m".
function lastNameCases (lastName: string, gender: TypeGender = 'm'): INameCases {
  // Женский пол ?
  const isFemale = gender === 'f'

  switch (lastName.substring(lastName.length - 3).toLowerCase()) {
    case 'иец': case 'йец': // Коломиец
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'йца',
        dat: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'йцу',
        acc: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'йца',
        inst: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'йцем',
        prep: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'йце',
      }

    case 'лец': // Елец
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'ьца',
        dat: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'ьцу',
        acc: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'ьца',
        inst: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'ьцом',
        prep: isFemale ? lastName : lastName.substring(0, lastName.length - 2) + 'ьце',
      }
  }

  switch (lastName.substring(lastName.length - 2).toLowerCase()) {
    case 'на': // Сталина
    case 'ва': // Хрущёва, Жданова, Конева
      return {
        nom: lastName,
        gen: lastName.substring(0, lastName.length - 1) + 'ой',
        dat: lastName.substring(0, lastName.length - 1) + 'ой',
        acc: lastName.substring(0, lastName.length - 1) + 'у',
        inst: lastName.substring(0, lastName.length - 1) + 'ой',
        prep: lastName.substring(0, lastName.length - 1) + 'ой',
      }

    case 'ин': // Сталин
    case 'ёв': // Хрущёв
    case 'ов': // Жданов
    case 'ев': // Конев
      return {
        nom: lastName,
        gen: lastName + 'а',
        dat: lastName + 'у',
        acc: lastName + 'а',
        inst: lastName + 'ым',
        prep: lastName + 'е',
      }

    case 'ая': // Троцкая
      return {
        nom: lastName,
        gen: lastName.substring(0, lastName.length - 2) + 'ой',
        dat: lastName.substring(0, lastName.length - 2) + 'ой',
        acc: lastName.substring(0, lastName.length - 2) + 'ую',
        inst: lastName.substring(0, lastName.length - 2) + 'ой',
        prep: lastName.substring(0, lastName.length - 2) + 'ой',
      }

    case 'ий': // Троцкий
    case 'ый': // Подгорный
      return {
        nom: lastName,
        gen: lastName.substring(0, lastName.length - 2) + 'ого',
        dat: lastName.substring(0, lastName.length - 2) + 'ому',
        acc: lastName.substring(0, lastName.length - 2) + 'ого',
        inst: lastName.substring(0, lastName.length - 1) + 'м',
        prep: lastName.substring(0, lastName.length - 2) + 'ом',
      }

    case 'ей': // Еремей
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'я',
        dat: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'ю',
        acc: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'я',
        inst: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'ем',
        prep: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'е',
      }

    case 'ич': // Каганович
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName + 'а',
        dat: isFemale ? lastName : lastName + 'у',
        acc: isFemale ? lastName : lastName + 'а',
        inst: isFemale ? lastName : lastName + 'ем',
        prep: isFemale ? lastName : lastName + 'е',
      }

    case 'ик': // Шверник
    case 'ян': // Микоян
    case 'ец': // Чабрец
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName + 'а',
        dat: isFemale ? lastName : lastName + 'у',
        acc: isFemale ? lastName : lastName + 'а',
        inst: isFemale ? lastName : lastName + 'ом',
        prep: isFemale ? lastName : lastName + 'е',
      }

    case 'ия': // Берия
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'и',
        dat: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'и',
        acc: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'ю',
        inst: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'ей',
        prep: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'и',
      }
  }

  switch (lastName.substring(lastName.length - 1).toLowerCase()) {
    case 'б': case 'п':
    case 'в': case 'ф':
    case 'г': case 'к':
    case 'д': case 'т':
    case 'з': case 'с':
    case 'х':
    case 'н':
    case 'р':
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName + 'а',
        dat: isFemale ? lastName : lastName + 'у',
        acc: isFemale ? lastName : lastName + 'а',
        inst: isFemale ? lastName : lastName + 'ом',
        prep: isFemale ? lastName : lastName + 'е',
      }

    case 'ц':
    case 'ж': case 'ш': case 'щ':
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName + 'а',
        dat: isFemale ? lastName : lastName + 'у',
        acc: isFemale ? lastName : lastName + 'а',
        inst: isFemale ? lastName : lastName + 'ем',
        prep: isFemale ? lastName : lastName + 'е',
      }

    case 'ь': // Чубарь
      return {
        nom: lastName,
        gen: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'я',
        dat: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'ю',
        acc: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'я',
        inst: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'ем',
        prep: isFemale ? lastName : lastName.substring(0, lastName.length - 1) + 'е',
      }
  }

  return { nom: lastName, gen: lastName, dat: lastName, acc: lastName, inst: lastName, prep: lastName }
}

// Просклонять имя из именительного в остальные падежи.
// В некоторых случаях пригодится хинт gender, равный "f" или (по умолчанию) "m".
function firstNameCases (firstName: string, gender: TypeGender = 'm'): INameCases {
  // Женский пол ?
  const isFemale = gender === 'f'

  switch (firstName.toLowerCase()) {
    case 'лев':
      return { nom: 'Лев', gen: 'Льва', dat: 'Льву', acc: 'Льва', inst: 'Львом', prep: 'Льве' }
    case 'петр': case 'пётр':
      return { nom: 'Пётр', gen: 'Петра', dat: 'Петру', acc: 'Петра', inst: 'Петром', prep: 'Петре' }
    case 'павел':
      return { nom: 'Павел', gen: 'Павла', dat: 'Павлу', acc: 'Павла', inst: 'Павлом', prep: 'Павле' }
  }

  switch (firstName.substring(firstName.length - 2).toLowerCase()) {
    case 'ай': // Николай
    case 'ей': // Андрей, Сергей
      return {
        nom: firstName,
        gen: firstName.substring(0, firstName.length - 1) + 'я',
        dat: firstName.substring(0, firstName.length - 1) + 'ю',
        acc: firstName.substring(0, firstName.length - 1) + 'я',
        inst: firstName.substring(0, firstName.length - 1) + 'ем',
        prep: firstName.substring(0, firstName.length - 1) + 'е',
      }

    case 'ий': // Дмитрий, Григорий
      return {
        nom: firstName,
        gen: firstName.substring(0, firstName.length - 1) + 'я',
        dat: firstName.substring(0, firstName.length - 1) + 'ю',
        acc: firstName.substring(0, firstName.length - 1) + 'я',
        inst: firstName.substring(0, firstName.length - 1) + 'ем',
        prep: firstName.substring(0, firstName.length - 1) + 'и',
      }

    case 'ья': // Илья, Дарья, Наталья
      return {
        nom: firstName,
        gen: firstName.substring(0, firstName.length - 1) + 'и',
        dat: firstName.substring(0, firstName.length - 1) + 'е',
        acc: firstName.substring(0, firstName.length - 1) + 'ю',
        inst: firstName.substring(0, firstName.length - 1) + (isFemale ? 'ей' : 'ёй'),
        prep: firstName.substring(0, firstName.length - 1) + 'е',
      }
  }

  switch (firstName.substring(firstName.length - 1).toLowerCase()) {
    case 'а': // Анна
      return {
        nom: firstName,
        gen: firstName.substring(0, firstName.length - 1) + 'ы',
        dat: firstName.substring(0, firstName.length - 1) + 'е',
        acc: firstName.substring(0, firstName.length - 1) + 'у',
        inst: firstName.substring(0, firstName.length - 1) + 'ой',
        prep: firstName.substring(0, firstName.length - 1) + 'е',
      }

    case 'я': // Мария
      return {
        nom: firstName,
        gen: firstName.substring(0, firstName.length - 1) + 'и',
        dat: firstName.substring(0, firstName.length - 1) + 'и',
        acc: firstName.substring(0, firstName.length - 1) + 'ю',
        inst: firstName.substring(0, firstName.length - 1) + 'ей',
        prep: firstName.substring(0, firstName.length - 1) + 'и',
      }

    case 'б': case 'п':
    case 'в': case 'ф':
    case 'г': case 'к':
    case 'д': case 'т':
    case 'з': case 'с':
    case 'л': case 'м':
    case 'н': case 'р':
    case 'х': case 'ц':
      return {
        nom: firstName,
        gen: firstName + 'а',
        dat: firstName + 'у',
        acc: firstName + 'а',
        inst: firstName + 'ом',
        prep: firstName + 'е',
      }

    case 'ж': case 'ш': case 'щ': case 'ч':
      return {
        nom: firstName,
        gen: firstName + 'а',
        dat: firstName + 'у',
        acc: firstName + 'а',
        inst: firstName + 'ем',
        prep: firstName + 'е',
      }

    case 'ь': // Лазарь
      return {
        nom: firstName,
        gen: isFemale ? firstName : firstName.substring(0, firstName.length - 1) + 'я',
        dat: isFemale ? firstName : firstName.substring(0, firstName.length - 1) + 'ю',
        acc: isFemale ? firstName : firstName.substring(0, firstName.length - 1) + 'я',
        inst: isFemale ? firstName : firstName.substring(0, firstName.length - 1) + 'ем',
        prep: isFemale ? firstName : firstName.substring(0, firstName.length - 1) + 'е',
      }
  }

  return { nom: firstName, gen: firstName, dat: firstName, acc: firstName, inst: firstName, prep: firstName }
}

// Просклонять отчество из именительного в остальные падежи.
function patronymicCases (patronymic: string): INameCases {
  if (!patronymic) {
    return { nom: '', gen: '', dat: '', acc: '', inst: '', prep: '' }
  }

  if (patronymic.includes(' ') || patronymic.includes('-')) {
    // Сложное отчество (напр., Тимер улы) не склоняем.
    return { nom: patronymic, gen: patronymic, dat: patronymic, acc: patronymic, inst: patronymic, prep: patronymic }
  }

  switch (patronymic.substring(patronymic.length - 2).toLowerCase()) {
    case 'на': // Ивановна, Ильинична
      return {
        nom: patronymic,
        gen: patronymic.substring(0, patronymic.length - 1) + 'ы',
        dat: patronymic.substring(0, patronymic.length - 1) + 'е',
        acc: patronymic.substring(0, patronymic.length - 1) + 'у',
        inst: patronymic.substring(0, patronymic.length - 1) + 'ой',
        prep: patronymic.substring(0, patronymic.length - 1) + 'е',
      }
  }

  switch (patronymic.substring(patronymic.length - 3).toLowerCase()) {
    case 'вич': // Иванович
      return {
        nom: patronymic,
        gen: patronymic + 'а',
        dat: patronymic + 'у',
        acc: patronymic + 'а',
        inst: patronymic + 'ем',
        prep: patronymic + 'е',
      }

    case 'нич':
    case 'мич': // Кузьмич
    case 'ьич': // Ильич
      return {
        nom: patronymic,
        gen: patronymic + 'а',
        dat: patronymic + 'у',
        acc: patronymic + 'а',
        inst: patronymic + 'ом',
        prep: patronymic + 'е',
      }
  }

  return { nom: patronymic, gen: patronymic, dat: patronymic, acc: patronymic, inst: patronymic, prep: patronymic }
}

// Если входная строка выглядит как название организации, а не ФИО — возвращает true.
function looksLikeOrg (name: string): boolean {
  // Кавычки-ёлочки — однозначный признак организации.
  if (/«[^»]*»/.test(name)) return true

  // Первое слово — аббревиатура ОПФ (ООО, МБОУ…).
  // «ИП» намеренно исключаем: «ИП Иванов Иван Иванович» — это ФИО с префиксом.
  const firstWord = (name.split(/\s+/)[0] ?? '').toUpperCase()

  if (firstWord !== 'ИП' && ORG_ABBREVIATIONS.has(firstWord)) return true

  // Начинается с известного полного наименования ОПФ.
  const lower = name.toLowerCase()
  if (ORG_FORM_ENTRIES.some((e) => lower.startsWith(e.key))) return true

  return false
}

/**
 * Склоняет ФИО по всем падежам.
 *
 * Если в строку попало название организации (содержит «», начинается с аббревиатуры
 * ОПФ или полного наименования типа «Общество с ограниченной…»), автоматически
 * делегирует склонение в orgNameCases.
 *
 * @param {string} fullName  - ФИО (строго в таком порядке!) в именительном падеже (1, 2 или 3 слова).
 *                             Поддерживается префикс «ИП » (сохраняется во всех формах).
 * @param {'m'|'f'} [gender] - пол, по умолчанию "m".
 * @returns {INameCases}
 */

function fullNameCases (fullName: string, gender: TypeGender = 'm'): INameCases {
  const trimmed = fullName.trim()

  if (looksLikeOrg(trimmed)) {
    return orgNameCases(trimmed)
  }

  let prefix = ''
  let name = trimmed

  if (name.startsWith('ИП ')) {
    prefix = 'ИП'
    name = name.substring(3).trimStart()
  }

  let parts = name.split(' ').map((s) => s.trim()).filter(Boolean)

  if (parts.length > 3) {
    parts = [parts[0] ?? '', parts[1] ?? '', parts.slice(2).join(' ')]
  }
  while (parts.length < 3) {
    parts.push('')
  }

  const [last = '', first = '', patronymic = ''] = parts

  const lc = lastNameCases(last, gender)
  const fc = firstNameCases(first, gender)
  const pc = patronymicCases(patronymic)

  const result = {} as INameCases
  for (const c of ['nom', 'gen', 'dat', 'acc', 'inst', 'prep'] as const) {
    const joined = [lc[c], fc[c], pc[c]].filter(Boolean).join(' ')
    result[c] = prefix ? `${prefix} ${joined}` : joined
  }
  return result
}

export { fullNameCases, lastNameCases, firstNameCases, patronymicCases, orgNameCases }
